'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import type { YouTubeStatus } from '@/lib/youtube/types';

class YouTubeRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export function useYouTubeApi() {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const projectId = currentProject?.id || '';
  const request = useCallback(
    async <T>(
      path: string,
      options?: { params?: URLSearchParams; body?: Record<string, unknown>; signal?: AbortSignal },
    ): Promise<T> => {
      if (!user || !projectId) throw new Error('Select a project to continue.');
      const params = new URLSearchParams(options?.params);
      params.set('projectId', projectId);
      const token = await user.getIdToken();
      const response = await fetch(`${path}?${params}`, {
        method: options?.body ? 'POST' : 'GET',
        signal: options?.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: options?.body ? JSON.stringify({ ...options.body, projectId }) : undefined,
      });
      const result = await response.json();
      if (!response.ok)
        throw new YouTubeRequestError(
          typeof result.error === 'string' ? result.error : 'YouTube data could not be loaded.',
          response.status,
        );
      return result as T;
    },
    [user, projectId],
  );
  return { request, projectId, userId: user?.uid || '', ready: Boolean(user && projectId) };
}

export function useYouTubeResource<T>(path: string, params = '', enabled = true) {
  const { request, projectId, userId, ready } = useYouTubeApi();
  const [version, setVersion] = useState(0);
  const key = `${userId}:${projectId}:${path}:${params}`;
  const [result, setResult] = useState<{ key: string; value?: T; error?: string } | null>(null);
  useEffect(() => {
    if (!ready || !enabled) return;
    const controller = new AbortController();
    request<T>(path, { params: new URLSearchParams(params), signal: controller.signal })
      .then(value => {
        if (!controller.signal.aborted) setResult({ key, value });
      })
      .catch(error => {
        if (!controller.signal.aborted)
          setResult(previous => ({
            key,
            value:
              previous?.key === key &&
              !(error instanceof YouTubeRequestError && [401, 403].includes(error.status))
                ? previous.value
                : undefined,
            error: error.message,
          }));
      });
    return () => controller.abort();
  }, [ready, enabled, request, path, params, key, version]);
  const current = ready && enabled && result?.key === key ? result : null;
  const reload = useCallback(() => setVersion(value => value + 1), []);
  return {
    data: current?.value || null,
    error: current?.error || null,
    loading: enabled && ready && !current,
    reload,
  };
}

export function useYouTubeStatus() {
  const resource = useYouTubeResource<YouTubeStatus>('/api/integrations/youtube');
  const connected = resource.data?.connected;
  const pending = resource.data && ['queued', 'running'].includes(resource.data.status);
  useEffect(() => {
    if (!connected) return;
    const timer = setInterval(resource.reload, pending ? 20_000 : 60_000);
    return () => clearInterval(timer);
  }, [connected, pending, resource.reload]);
  return resource;
}
